import React, { useState, CSSProperties, useEffect } from 'react';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { Input } from 'antd';

interface SearchInputProps {
  placeholder?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  style?: CSSProperties;
  prefixIcon?: React.ReactNode;
  width?: string;
  borderWidth?: string;
  onPressEnter?: (value: string) => void;
  formatPaste?: boolean;
  autoFocus?: boolean;
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = 'Search...',
  defaultValue = '',
  autoFocus,
  onChange,
  width,
  onClear,
  style = {},
  prefixIcon,
  borderWidth = '1px',
  onPressEnter,
  formatPaste = false,
}) => {
  const [value, setValue] = useState<string>('');
  const [focused, setFocused] = useState<boolean>(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const val = e.target.value;
    setValue(val);
    onChange?.(val);
  };

  const handleClear = (): void => {
    setValue('');
    onChange?.('');
    onClear?.();
  };

  const formatPastedValue = (raw: string): string => {
    return raw
      .replace(/;/g, ',')
      .replace(/\t+/g, ' ')
      .split(/\r?\n/)
      .flatMap((line) => line.split(','))
      .map((token) => token.trim())
      .filter(Boolean)
      .join(',');
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    if (!formatPaste) {
      return;
    }

    e.preventDefault();
    const raw = e.clipboardData.getData('text');
    const formatted = formatPastedValue(raw);
    setValue(formatted);
    onChange?.(formatted);
  };

  return (
    <div
      className='custom-form'
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0 9px',
        border:
          focused || value
            ? `${borderWidth} solid var(--text-primary)`
            : 'none',
        backgroundColor:
          !focused && !value ? 'var(--light-gray)' : 'var(--surface-container)',
        borderRadius: '999px',
        width: width || '200px',
        height: '28px',
        transition: 'all 0.2s ease',
        ...style,
      }}
    >
      {prefixIcon ?? (
        <SearchOutlined
          style={{
            color: 'var(--text-primary)',
            fontSize: 12,
            fontWeight: '600',
          }}
        />
      )}

      <Input
        className='custom-black-input'
        value={value}
        autoFocus={autoFocus}
        onChange={handleChange}
        onPaste={handlePaste}
        onPressEnter={() => onPressEnter?.(value.trim())}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        bordered={false}
        style={{
          outline: 'none',
          boxShadow: 'none',
          background: 'transparent',
          padding: 0,
          height: '100%',
          fontSize: 12,
          color: 'var(--text-primary)',
        }}
      />

      {value && (
        <div
          onClick={handleClear}
          style={{
            backgroundColor: 'var(--text-primary)',
            borderRadius: '50%',
            padding: 8,
            width: 10,
            height: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <CloseOutlined style={{ color: 'var(--white)', fontSize: 8 }} />
        </div>
      )}
    </div>
  );
};

export default SearchInput;
